import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import ResponsiveContainer from '@/components/ResponsiveContainer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '../utils/toast';
import { ClipboardCheck, Users, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { sortObjectOptions } from '@/utils/sortOptions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EvaluationTriggerSimplified = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Data
  const [hospitals, setHospitals] = useState([]);
  const [managers, setManagers] = useState([]);
  
  // Form
  const [selectedHospital, setSelectedHospital] = useState('');
  const [selectedManagers, setSelectedManagers] = useState([]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      // Super Admin, Exlva Admin, and Hospital Admin can trigger evaluations
      if (!['Super Admin', 'Exlva Admin', 'Hospital Admin'].includes(parsedUser.role)) {
        toast.error('Only admins can trigger evaluations');
        navigate('/dashboard');
        return;
      }
      
      fetchHospitals();
    }
  }, [navigate]);
  
  // Auto-select hospital for Hospital Admin
  useEffect(() => {
    if (user && user.role === 'Hospital Admin' && hospitals.length > 0) {
      // Auto-select the hospital admin's own hospital
      const ownHospital = hospitals[0]; // Hospital Admin only sees their own hospital
      setSelectedHospital(ownHospital.id);
      fetchManagers(ownHospital.id);
    }
  }, [user, hospitals]);

  const fetchHospitals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/evaluations/hospitals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show ALL hospitals (both licensed and non-licensed)
      setHospitals(response.data.hospitals || []);
    } catch (error) {
      console.error('Error fetching hospitals:', error);
      toast.error('Failed to load hospitals');
    }
  };

  const fetchManagers = async (hospitalId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/evaluations/managers-by-hospital/${hospitalId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setManagers(response.data.managers || []);
    } catch (error) {
      console.error('Error fetching managers:', error);
      toast.error('Failed to load managers');
      setManagers([]);
    }
  };

  const handleHospitalChange = (hospitalId) => {
    setSelectedHospital(hospitalId);
    setSelectedManagers([]);
    setManagers([]);
    
    if (hospitalId) {
      fetchManagers(hospitalId);
    }
  };

  const handleManagerToggle = (managerId) => {
    setSelectedManagers(prev => {
      if (prev.includes(managerId)) {
        return prev.filter(id => id !== managerId);
      } else {
        return [...prev, managerId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedManagers.length === managers.length) {
      setSelectedManagers([]);
    } else {
      setSelectedManagers(managers.map(m => m.user_id));
    }
  };

  const handleTrigger = async () => {
    if (!selectedHospital) {
      toast.error('Please select a hospital');
      return;
    }
    
    if (selectedManagers.length === 0) {
      toast.error('Please select at least one manager');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create evaluation instances for each selected manager
      const payload = {
        hospital_admin_id: selectedHospital,
        manager_user_ids: selectedManagers,
        status: 'pending'
      };
      
      await axios.post(
        `${API}/evaluations/trigger-for-managers`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Evaluation triggered for ${selectedManagers.length} manager(s)`);
      
      // Reset form
      setSelectedHospital('');
      setSelectedManagers([]);
      setManagers([]);
      
      // Navigate back to dashboard
      setTimeout(() => {
        navigate('/evaluations');
      }, 1500);
      
    } catch (error) {
      console.error('Error triggering evaluation:', error);
      toast.error(error.response?.data?.detail || 'Failed to trigger evaluation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <ResponsiveContainer>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/evaluations')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6 text-[#0056B3]" />
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">
                Trigger New Evaluation
              </h1>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <Card className="p-6 md:p-8">
          <div className="space-y-6">
            {/* Step 1: Select Hospital - Only show for Super Admin and Exlva Admin */}
            {user && ['Super Admin', 'Exlva Admin'].includes(user.role) && (
              <div>
                <Label className="text-base font-semibold mb-3 block">
                  1. Select Hospital <span className="text-red-500">*</span>
                </Label>
                <Select value={selectedHospital} onValueChange={handleHospitalChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose a hospital" />
                  </SelectTrigger>
                  <SelectContent>
                    {hospitals.length === 0 ? (
                      <div className="px-2 py-1 text-sm text-gray-500">
                        No hospitals available
                      </div>
                    ) : (
                      sortObjectOptions(hospitals, 'institution_name').map((hospital) => (
                        <SelectItem key={hospital.id} value={hospital.id}>
                          <div className="flex items-center gap-2 w-full">
                            <span>{hospital.institution_name}</span>
                            {hospital.is_licensed ? (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                Licensed
                              </span>
                            ) : (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                Non-Licensed
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Both licensed and non-licensed hospitals are shown
                </p>
              </div>
            )}
            
            {/* Hospital Admin sees their hospital name (read-only) */}
            {user && user.role === 'Hospital Admin' && (
              <div>
                <Label className="text-base font-semibold mb-3 block">
                  Your Hospital
                </Label>
                <div className="w-full p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {user.institution_name || 'Your Hospital'}
                    </span>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                      Your Hospital
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  You can only trigger evaluations for your hospital's reporting managers
                </p>
              </div>
            )}

            {/* Step 2: Select Managers (Step 1 for Hospital Admin) */}
            {selectedHospital && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">
                    {user && user.role === 'Hospital Admin' ? '1' : '2'}. Select Reporting Managers <span className="text-red-500">*</span>
                  </Label>
                  {managers.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                    >
                      {selectedManagers.length === managers.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  )}
                </div>

                {managers.length === 0 ? (
                  <div className="border rounded-lg p-6 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No managers found for this hospital</p>
                  </div>
                ) : (
                  <div className="border rounded-lg max-h-96 overflow-y-auto">
                    {managers.map((manager) => (
                      <div
                        key={manager.user_id}
                        className="flex items-center gap-3 p-4 hover:bg-gray-50 border-b last:border-b-0"
                      >
                        <Checkbox
                          checked={selectedManagers.includes(manager.user_id)}
                          onCheckedChange={() => handleManagerToggle(manager.user_id)}
                        />
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => handleManagerToggle(manager.user_id)}
                        >
                          <p className="font-medium text-gray-900">{manager.full_name}</p>
                          <p className="text-sm text-gray-500">{manager.email}</p>
                          {manager.department_name && (
                            <p className="text-xs text-gray-400 mt-1">
                              {manager.department_name} - {manager.job_function_name}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {selectedManagers.length > 0 && (
                  <p className="text-sm text-blue-600 mt-2">
                    {selectedManagers.length} manager(s) selected
                  </p>
                )}
              </div>
            )}

            {/* Trigger Button */}
            <div className="flex justify-end pt-4 border-t">
              <Button
                onClick={handleTrigger}
                disabled={loading || !selectedHospital || selectedManagers.length === 0}
                className="bg-[#0056B3] hover:bg-[#0056B3]/90 px-8"
              >
                {loading ? 'Triggering...' : `Trigger Evaluation${selectedManagers.length > 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </Card>

        {/* Instructions */}
        <Card className="mt-6 p-4 bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Selected managers will see the "Evaluation Form" tab in their menu</li>
            <li>• Each manager will evaluate their direct staff members</li>
            <li>• Evaluation status will be set to "Pending" initially</li>
            <li>• You can track progress from the Evaluation Dashboard</li>
          </ul>
        </Card>
      </ResponsiveContainer>
    </AdminLayout>
  );
};

export default EvaluationTriggerSimplified;
